import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Loader2, FileText, X, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Template {
  id: string;
  name: string;
  chat_suggestions: string[];
  created_at: string;
}

const Templates = () => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionInput, setSuggestionInput] = useState("");

  const fetchTemplates = async () => {
    const { data, error } = await supabase
      .from("template")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setTemplates((data as Template[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setSuggestions([]);
    setSuggestionInput("");
    setDialogOpen(true);
  };

  const openEdit = (t: Template) => {
    setEditId(t.id);
    setName(t.name);
    setSuggestions(t.chat_suggestions || []);
    setSuggestionInput("");
    setDialogOpen(true);
  };

  const addSuggestion = () => {
    const val = suggestionInput.trim();
    if (!val) return;
    if (suggestions.includes(val)) {
      toast({ title: "Duplicate", description: "This suggestion already exists.", variant: "destructive" });
      return;
    }
    setSuggestions((prev) => [...prev, val]);
    setSuggestionInput("");
  };

  const removeSuggestion = (s: string) => {
    setSuggestions((prev) => prev.filter((x) => x !== s));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: "Required", description: "Template name is required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { name: name.trim(), chat_suggestions: suggestions };
    let error;
    if (editId) {
      ({ error } = await supabase.from("template").update(payload).eq("id", editId));
    } else {
      ({ error } = await supabase.from("template").insert({ ...payload, client_id: profile?.client_id ?? "" }));
    }
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editId ? "Updated" : "Created", description: `Template "${name.trim()}" saved.` });
      setDialogOpen(false);
      fetchTemplates();
    }
    setSaving(false);
  };

  const deleteTemplate = async (t: Template) => {
    const { error } = await supabase.from("template").delete().eq("id", t.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      toast({ title: "Deleted", description: `Template "${t.name}" removed.` });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Templates</h1>
          <p className="text-sm text-muted-foreground">Manage templates and their chat suggestions.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No templates yet</CardTitle>
          <CardDescription>Create your first template to get started.</CardDescription>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Suggestions</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {t.chat_suggestions.length > 0 ? (
                        t.chat_suggestions.slice(0, 3).map((s, i) => (
                          <Badge key={i} variant="secondary" className="text-xs max-w-48 truncate">
                            {s}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                      {t.chat_suggestions.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{t.chat_suggestions.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => deleteTemplate(t)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>
              {editId ? "Update the template and its chat suggestions." : "Create a new template with chat suggestions."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="template-name">Name *</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sales Analysis"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="suggestion-input">Chat Suggestions</Label>
              <div className="flex gap-2">
                <Input
                  id="suggestion-input"
                  value={suggestionInput}
                  onChange={(e) => setSuggestionInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSuggestion(); } }}
                  placeholder="e.g. Show me top revenue drivers"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addSuggestion}>Add</Button>
              </div>
              {suggestions.length > 0 && (
                <div className="flex flex-col gap-1 mt-1 max-h-40 overflow-y-auto">
                  {suggestions.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-sm">
                      <span className="flex-1 truncate">{s}</span>
                      <button onClick={() => removeSuggestion(s)} className="shrink-0 rounded-full hover:bg-destructive/20 p-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editId ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
